import './fixed-submit-bar.scss';
import {observer} from "mobx-react";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {Button} from "../../../components/inputs/button/button";
import {LABEL_POSITIONS, Toggle} from "../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {CreateRmaMainStore} from "../stores/create-rma-main-store";
import {IValidationGroup} from '../../../hooks/validation-group/validation-group-hook';
import {
    CREATE_RMA_SUBMIT_BLOCK_REASONS,
} from "../../manage-returns/rma-page/rma-types";
import currentAccountStore from '../../accounts/stores/current-account-store';
import currentPortalStore from '../../../stores/current-portal-store';
import { PERMISSION } from '../../../models/PermissionsTypes';
import { CREATE_RMA_MODE } from '../../../services/api/create-rma-service';
import BaseLayoutStickyToolbar
    from "../../../components/layouts/base-page-layout/base-layout-sticky-toolbar/base-layout-sticky-toolbar";
import { CreateRmaSubmitBlockTooltipWrapper } from './create-rma-paid-rma-tooltip';


interface IProps {
    createRmaMainStore: CreateRmaMainStore;
    validtionGroup: IValidationGroup;
}

const ButtonsGodMode: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isCreatingRma = createRmaMainStore.isCreatingRma;
    const rmaRequestStore = createRmaMainStore.rmaRequestStore;
    const orderStore = createRmaMainStore.orderStore;
    const balancePreviewStore = rmaRequestStore?.balancePreviewStore;
    const isBlockedDueToCustomerPayment = !!balancePreviewStore?.isCreateRmaBlocked
    const dynamicShippingOptionsStore = rmaRequestStore?.dynamicShippingOptionsStore;
    const pageScope = 'dashboard-create-rma';
    const { getTranslation } = useDashboardTranslation();

    const shouldShowOutOfPolicyApproveTooltip = (): boolean => {
        /** when the request is not yet constructed - we cannot say anything yet about disabling button/eligibility */
        if (rmaRequestStore?.requestProducts.length === 0) {
            return false;
        }

        /** when "General Returns" setting "Require Manager Approval" is OFF */
        if (!currentPortalStore.requireManagerApproval()) {
            return false;
        }

        /** if request is eligible - do not disable */
        if (!(rmaRequestStore?.eligibilityStore.isOutOfPolicy ?? false)) {
            return false;
        }

        /** disable for users without permissions */
        if (currentAccountStore.hasPermission(PERMISSION.APPROVE_RMA_AS_MANAGER) || currentAccountStore.hasPermission(PERMISSION.PROMPT_MANGER_APPROVAL)) {
            return false;
        }

        return true;
    };

    const getApproveSubmitBlockReason = (): CREATE_RMA_SUBMIT_BLOCK_REASONS | undefined => {
        if (isBlockedDueToCustomerPayment) {
            return CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED;
        }
        if (shouldShowOutOfPolicyApproveTooltip()) {
            return CREATE_RMA_SUBMIT_BLOCK_REASONS.OUT_OF_POLICY_NO_APPROVE_PERMISSION;
        }
        return undefined;
    };

    const shouldShowDisabledButtonWithTooltip = (): boolean => {
        return getApproveSubmitBlockReason() !== undefined;
    };

    const shouldWaitForBalancePreview = !!balancePreviewStore?.isInProgress && (rmaRequestStore?.requestProducts.length ?? 0) > 0;
    const disableSubmitButton = rmaRequestStore?.requestProducts.length === 0
        || isCreatingRma
        || !validtionGroup.isValid()
        || createRmaMainStore.rmaRequestStore?.eligibilityStore.isInProgress
        || shouldWaitForBalancePreview
        || orderStore?.isCreateRmaNotAllowed
        || isBlockedDueToCustomerPayment
        || dynamicShippingOptionsStore?.isLoading
        || dynamicShippingOptionsStore?.shouldSelectShippingOption;
    const approveSubmitBlockReason = getApproveSubmitBlockReason();
    const pendingSubmitBlockReason = isBlockedDueToCustomerPayment
        ? CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED
        : undefined;
    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={pendingSubmitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA_AS_PENDING', pageScope)}
                    disabled={disableSubmitButton}
                    hasLoader={true}
                    isLoading={isCreatingRma && !createRmaMainStore.rmaRequestStore?.isAutoApprove}
                    onClick={() => {
                        createRmaMainStore.rmaRequestStore?.setIsAutoApprove(false);
                        createRmaMainStore.createRma();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={approveSubmitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA_AS_APPROVED', pageScope)}
                    disabled={shouldShowDisabledButtonWithTooltip() ? true : disableSubmitButton}
                    hasLoader={true}
                    isLoading={isCreatingRma && createRmaMainStore.rmaRequestStore?.isAutoApprove}
                    onClick={() => {
                        createRmaMainStore.rmaRequestStore?.setIsAutoApprove(true);
                        createRmaMainStore.createRma();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
        </span>
    );
});

const ButtonPolicyLed: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isCreatingRma = createRmaMainStore.isCreatingRma;
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const rmaRequestStore = createRmaMainStore.rmaRequestStore;
    const balancePreviewStore = createRmaMainStore.rmaRequestStore?.balancePreviewStore;
    const isBlockedDueToCustomerPayment = !!balancePreviewStore?.isCreateRmaBlocked

    const dynamicShippingOptionsStore = rmaRequestStore?.dynamicShippingOptionsStore;
    const shouldWaitForBalancePreview = !!balancePreviewStore?.isInProgress && (rmaRequestStore?.requestProducts.length ?? 0) > 0;
    const shouldDisableSubmitButton = rmaRequestStore?.requestProducts.length === 0 || isCreatingRma
        || !validtionGroup.isValid() || createRmaMainStore.rmaRequestStore?.eligibilityStore.isOutOfPolicy
        || createRmaMainStore.rmaRequestStore?.eligibilityStore.isInProgress || createRmaMainStore.orderStore?.isCreateRmaNotAllowed
        || dynamicShippingOptionsStore?.isLoading || dynamicShippingOptionsStore?.shouldSelectShippingOption || isBlockedDueToCustomerPayment || shouldWaitForBalancePreview;

        const submitBlockReason = isBlockedDueToCustomerPayment
        ? CREATE_RMA_SUBMIT_BLOCK_REASONS.CUSTOMER_PAYMENT_REQUIRED
        : undefined;

    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <CreateRmaSubmitBlockTooltipWrapper blockReason={submitBlockReason} getTranslation={getTranslation}>
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                    text={getTranslation('SUBMIT_RMA', pageScope)}
                    disabled={shouldDisableSubmitButton}
                    hasLoader={true}
                    isLoading={isCreatingRma && !createRmaMainStore.rmaRequestStore?.isAutoApprove}
                    onClick={() => {
                        createRmaMainStore.rmaRequestStore?.setIsAutoApprove(false);
                        createRmaMainStore.createRma();
                    }}
                />
            </CreateRmaSubmitBlockTooltipWrapper>
        </span>
    );
});


const FixedSubmitBar: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
	const rmaRequestStore = createRmaMainStore.rmaRequestStore;
	const isCustomerEmailMissing = !createRmaMainStore.orderStore?.customer?.email;

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';

	return (
		<BaseLayoutStickyToolbar>
            <span>
                <Toggle label={getTranslation('SEND_CUSTOMER_EMAILS', pageScope)}
                        checked={rmaRequestStore?.sendCustomerEmails}
                        onChange={rmaRequestStore?.setSendCustomerEmail}
                        labelPosition={LABEL_POSITIONS.LEFT}
                        disabled={isCustomerEmailMissing}/>
            </span>

            { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE &&
                <ButtonsGodMode createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} />
            }
            { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE &&
                <ButtonPolicyLed createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} />
            }

        </BaseLayoutStickyToolbar>
	);
});

export default FixedSubmitBar;
